import { useCallback } from 'react';
import { useSWRConfig } from 'swr';

import { useActionItemsRefresh } from '~/contexts/actionItemsRefreshContext';

export const TASKS_KEY_PREFIX = 'tasks';

export type TaskScope = {
  propertyId?: string;
  portfolioId?: string;
  carId?: string;
  portfolios?: readonly string[];
  regions?: readonly string[];
};

export const taskListKey = (scope: TaskScope) =>
  [
    TASKS_KEY_PREFIX,
    'list',
    scope.propertyId ?? null,
    scope.portfolioId ?? null,
    scope.carId ?? null,
    scope.portfolios?.join(',') ?? null,
    scope.regions?.join(',') ?? null,
  ] as const;

export const TASK_STATUSES_KEY = [TASKS_KEY_PREFIX, 'statuses'] as const;

export const TASK_LOG_LABELS_KEY = [TASKS_KEY_PREFIX, 'log-labels'] as const;

export const taskDetailKey = (taskId: string) => [TASKS_KEY_PREFIX, 'detail', taskId] as const;

const isTaskKey = (key: unknown) => Array.isArray(key) && key[0] === TASKS_KEY_PREFIX;

/**
 * Refetch every task cache after a task write, and the Needs Attention list and
 * badge with them. Closing, moving, re-dating or creating a task, removing a status
 * that moves its tasks, or publishing a commitment that opens a follow-up, all change
 * which follow-ups Needs Attention shows, and the badge would otherwise keep the old
 * count until its next poll. Writes that can't (a log label, a status rename) refetch
 * it too, which costs a refetch of the loaded queue but means a new task write can't
 * forget the refresh. Call it only after the write has succeeded.
 */
export function useRevalidateTasks() {
  const { mutate } = useSWRConfig();
  const { refresh: refreshActionItems } = useActionItemsRefresh();
  return useCallback(async () => {
    refreshActionItems();
    await mutate(isTaskKey);
  }, [mutate, refreshActionItems]);
}
